import React, { useEffect, useState } from 'react';
import { getSearchTips, getErrorSpecificTips } from '../services/searchTipsService';
import { getRandomIllustration } from './searchIllustrations';
import '../styles/searchStates.css';

/**
 * SearchFailedState Component
 * 
 * Displays an animated, user-friendly failed search state with helpful tips.
 * 
 * @param {Object} props
 * @param {string} props.query - The search query that failed
 * @param {string} props.errorType - Type of error (optional)
 * @param {Function} props.onRetry - Callback function when user clicks retry
 * @param {Function} props.onBack - Callback function when user clicks back
 */
const SearchFailedState = ({ query, errorType = 'NO_RESULTS', retryAfter = null, onRetry, onBack }) => {
    const [tips, setTips] = useState([]);
    const [IllustrationComponent, setIllustrationComponent] = useState(null);

    useEffect(() => {
        // Get contextual tips based on query and error type
        const contextualTips = getErrorSpecificTips(errorType, query);
        setTips(contextualTips);
        
        // Get random illustration
        const RandomIllustration = getRandomIllustration();
        setIllustrationComponent(() => RandomIllustration);
    }, [query, errorType]);

    const isUnavailable = errorType === 'SEARCH_TEMPORARILY_UNAVAILABLE';
    const isTimeout = errorType === 'TIMEOUT';
    const isRateLimit = errorType === 'RATE_LIMIT_EXCEEDED';
    const isUnauthorized = errorType === 'UNAUTHORIZED';
    const isNetwork = errorType === 'NETWORK_ERROR' || errorType === 'UNKNOWN';
    const title = isRateLimit
        ? 'Too many searches'
        : isUnauthorized
            ? 'Sign in again'
            : isUnavailable
                ? 'Search is temporarily unavailable'
                : isTimeout
                    ? 'Search timed out'
                    : isNetwork
                        ? "Couldn't reach LiveNow"
                        : 'No results found';
    const waitHint = retryAfter
        ? `Wait ${retryAfter} seconds, then try again.`
        : 'Try again in a moment.';
    const subtitle = isRateLimit
        ? waitHint
        : isUnauthorized
            ? 'Your session expired. Log in and search again.'
            : (isUnavailable || isTimeout || isNetwork)
                ? 'Try again in a moment.'
                : (
                    <>
                        We couldn't find any live content for <span className="query-highlight">"{query}"</span>
                    </>
                );

    return (
        <div className="search-failed-state">
            <div className="search-failed-content">
                {/* Animated Illustration - Disabled for now */}
                {/* <div className="search-failed-illustration-container">
                    {IllustrationComponent && <IllustrationComponent />}
                </div> */}

                {/* Error Message */}
                <div className="search-failed-message">
                    <h2 className="search-failed-title">{title}</h2>
                    <p className="search-failed-subtitle">{subtitle}</p>
                </div>

                {/* Search Tips */}
                {tips.length > 0 && (
                    <div className="search-tips-container">
                        <h3 className="search-tips-title">Try these tips to improve your search:</h3>
                        <ul className="search-tips-list">
                            {tips.map((tip, index) => (
                                <li key={index} className="search-tip-item">
                                    <span className="tip-text">{tip}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="search-failed-actions">
                    {onRetry && (
                        <button 
                            className="search-retry-button" 
                            onClick={onRetry}
                            aria-label="Try search again"
                        >
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            >
                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                <path d="M3 21v-5h5" />
                            </svg>
                            Try Again
                        </button>
                    )}
                    {onBack && (
                        <button 
                            className="search-back-button" 
                            onClick={onBack}
                            aria-label="Go back to search"
                        >
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            >
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                            Back to Search
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchFailedState;

